import {RouteLeafRouting} from "@/my-leaf/routes/route-leaf-routing";
import {getDepth, getWindyWeather, showInfo} from "@/my-leaf/routes/components/route-utils";
import {drawDepths, drawTide} from "@/my-leaf/routes/components/route-graphs";
import {eventBus} from "@/main";
import dayjs from "dayjs";
import * as waypoints from "@/my-leaf/routes/components/route-waypoints";
import { offsetWP } from "@/my-leaf/routes/components/route-waypoints";
import L from "leaflet";
import * as legs from "@/my-leaf/routes/components/route-legs";
import {AIOLayer} from "@/my-leaf/layers/aio-layer";
import {NavareaLayer} from "@/my-leaf/layers/navarea-layer";
import { useFleetStore } from '@/stores/fleet';
import { GeoUtils } from "@/utils/geo-utils";
import { sleep } from "@/utils/utils";
import { createShipIcon } from '@/utils/shipIcon';

let contextMenuButtonCount = 0

export class RouteLeaf extends RouteLeafRouting {
    waypoints = []
    selectedWaypointIndex = undefined;
    isBaypassMode = false
    selecteBaypassWaypoints = []
    isAutoroutingCancel = false
    depthsList = null
    aioLayer = null                 // слой для отображения объектов AIO
    isAIOVisible = false            // флаг для управления видимостью слоя AIO
    navareaLayer = null             // слой для отображения объектов Navarea
    isNavareaVisible = false        // флаг для управления видимостью слоя Navarea

    constructor() {
        super();
        this.fleetStore = useFleetStore();
    }

    async init(el) {
        await super.initbaserouting(el);

        // fleetStore is already initialized in constructor

        // Принудительно сбрасываем флаги видимости overlay-ев при инициализации
        this.isAIOVisible = false;
        this.isNavareaVisible = false;

        this.centerOnSelectedShip();

        this.depthsList = new L.FeatureGroup();
        this.map.addLayer(this.depthsList);

        this.addNoGoLayer()
        
        // Redraw layers to pick up anything added during async init
        if (typeof this.drawMapLayers === 'function') {
            this.drawMapLayers();
        }

        // Создаем panes для AIO и Navarea объектов
        this.createAIOPanes();
        this.createNavareaPanes();        
        // Инициализируем AIO слой
        this.aioLayer = new AIOLayer(this.map, null);
        
        // Инициализируем Navarea слой
        this.navareaLayer = new NavareaLayer(this.map, null);
        
        // Принудительно очищаем все существующие оверлеи
        this.clearAllOverlays();

        // Слушаем события для управления маршрутом
        eventBus.$off('draw-global-route')
        eventBus.$on('draw-global-route', () => {
            eventBus.$emit('control-clear-route')
            this.startRouteCalculation([], [], true, true)
        })        
        
        // Слушаем события для управления отображением AIO
        eventBus.$off('toggle-aio-layer')
        eventBus.$on('toggle-aio-layer', (isVisible) => {
            this.toggleAIOLayer(isVisible)
        })

        // Слушаем события для отображения AIO на карте
        eventBus.$off('show-aio-on-map')
        eventBus.$on('show-aio-on-map', (aioItem) => {
            this.showAIOOnMap(aioItem)
        })

        // Слушаем события выбора AIO объекта на карте для дальнейшего выделения в таблице
        eventBus.$off('aio-item-selected-on-map')
        eventBus.$on('aio-item-selected-on-map', (aioItem) => {
            // Передаем событие в таблицу AIO для выделения строки выбраного объекта
            eventBus.$emit('highlight-aio-item-in-table', aioItem);
        })        
        
        // Слушаем события для управления отображением Navarea
        eventBus.$off('toggle-navarea-layer')
        eventBus.$on('toggle-navarea-layer', (isVisible) => {
            this.toggleNavareaLayer(isVisible)
        })

        eventBus.$off('show-navarea-on-map')
        eventBus.$on('show-navarea-on-map', (navareaItem) => {
            this.showNavareaOnMap(navareaItem)
        })        
        
        // Слушаем события выбора Navarea объекта на карте для дальнейшего выделения в таблице
        eventBus.$off('navarea-item-selected-on-map')
        eventBus.$on('navarea-item-selected-on-map', (navareaItem) => {
            // Передаем событие в таблицу Navarea для выделения строки выбранного объекта
            eventBus.$emit('highlight-navarea-item-in-table', navareaItem);
        })

        // Слушаем событие выделения Navarea объекта на карте
        eventBus.$off('highlight-navarea-on-map')
        eventBus.$on('highlight-navarea-on-map', (navareaItem) => {
            // Передаем событие в слой Navarea для выделения элементов
            if (this.navareaLayer) {
                this.navareaLayer.highlightNavareaElements(navareaItem);
            }
        })

        // Слушаем событие выделения AIO объекта на карте
        eventBus.$off('highlight-aio-on-map')
        eventBus.$on('highlight-aio-on-map', (aioId) => {
            // Передаем событие в слой AIO для выделения элементов
            if (this.aioLayer) {
                this.aioLayer.highlightAIOElements(aioId);
            }
        })        
        
        // Слушаем событие очистки выделения AIO
        eventBus.$off('clear-aio-highlight')
        eventBus.$on('clear-aio-highlight', () => {
            if (this.aioLayer) {
                this.aioLayer.clearHighlight();
            }
        })        
        
        // Обработчик клика по карте для очистки выделения
        this.map.on('click', () => {
            // Очищаем выделение AIO и Navarea при клике на карте
            eventBus.$emit('clear-aio-highlight');
            eventBus.$emit('clear-navarea-highlight');
            eventBus.$emit('clear-all-highlights');
        })

        // Обработчик клика правой кнопкой мыши по карте для контекстного меню
        this.map.on('contextmenu', (e) => {
            let id1 = 'map_depth' + contextMenuButtonCount;
            let id2 = 'map_info' + contextMenuButtonCount;
            let id3 = 'depths_info' + contextMenuButtonCount;
            let id4 = 'weather_info' + contextMenuButtonCount;
            let id5 = 'tide_graph' + contextMenuButtonCount;
            let id6 = 'depths_graph' + contextMenuButtonCount;
            let id7 = 'charts_graph' + contextMenuButtonCount;
            let id8 = 'charts_graph2' + contextMenuButtonCount;

            let wpCount = this.routesStore.getShipWaypointsCount

            let content =
                `<button id="` + id1 + `" onclick="" class="button-13">Depth</button>
                        <button id="` + id2 + `" onclick="" class="button-13">Info</button>
                        <button id="` + id3 + `" onclick="" class="button-13">Depths</button>
                        <button id="` + id4 + `" onclick="" class="button-13">Weather</button>
                        <button id="` + id5 + `" onclick="" class="button-13">Tide</button>`

            if (wpCount > 1)
                content += `&nbsp;<button id="` + id6 + `" onclick="" class="button-13">2D</button>`


            L.popup({autoClose: true, maxWidth: 500}).setLatLng(e.latlng).setContent(content).openOn(this.map);

            let coordinates = GeoUtils.normalizedLatLng(e.latlng);

            document.getElementById(id1)?.addEventListener('click', (e) => {
                this.map.closePopup()
                getDepth.call(this, coordinates)
            });

            document.getElementById(id2)?.addEventListener('click', (e) => {
                this.map.closePopup()
                showInfo(coordinates)
            });

            document.getElementById(id3)?.addEventListener('click', (e) => {
                this.map.closePopup()
                this.showDepths(coordinates)
            });

            document.getElementById(id4)?.addEventListener('click', (e) => {
                this.map.closePopup()

                let datetime = this.routesStore.getShipStartDate + ' ' + this.routesStore.getShipStartTime

                if (this.routesStore.getShipWaypointsCount === 0) {
                    const date = this.routesStore.getShipStartDate
                    const time = this.routesStore.getShipStartTime

                    const newDate = dayjs().add(this.routesStore.weatherSettings.dateOffset, 'days').format("YYYY-MM-DD");
                    const newTime = dayjs().add(this.routesStore.weatherSettings.timeOffset, 'hour').format("HH:mm");

                    datetime = dayjs(newDate + " " + newTime)
                }

                datetime = dayjs(datetime).unix()
                
                // Вызываем Windy Weather с контекстом карты
                this.showWindyWeather(coordinates, datetime, true)
            });

            document.getElementById(id5)?.addEventListener('click', (e) => {
                this.map.closePopup()
                drawTide.call(this, coordinates)

            });
            document.getElementById(id6)?.addEventListener('click', (e) => {
                this.map.closePopup()
                drawDepths.call(this)

            });

            contextMenuButtonCount++
        })

        this.restoreRoute()

    }

    get offsetWaypoints() {
        const offsets = GeoUtils.defaultOffsets;
        const waypoints = this.waypoints || [];

        return offsets.map(offset => {
            return waypoints.map(wp => offsetWP.call(this, wp, offset));
        });
    }

    offsetCurrentPoints(offset) {

    }

    // Метод для отображения погоды через Windy API
    async showWindyWeather(coordinates, time) {
        try {
            // Импортируем динамически чтобы избежать циклических зависимостей
            const { getWindyWeather } = await import("@/my-leaf/routes/components/route-utils");
            await getWindyWeather.call(this, coordinates, time);
        } catch (error) {
            console.error('Error showing Windy weather:', error);
        }
    }

    // Создаем маршрут из импортированных точек
    async createImportedRoute(points, callback) {
        for (let i = 0; i < points.length; i++) {
            const waypoint = points[i];

            waypoints.addWP.call(this, L.latLng(waypoint.lat, waypoint.lon), i)

            if (i % 50 == 0) {
                callback?.(i, points.length);
                await sleep(20);
            }
        }

        legs.drawLegs.call(this)
    }

    // Создаем таблицу маршрута
    makeRouteTable(updateRouteTable) {
        let route = []
        let info = []
        let safety = []
        let routeLength = 0;
        
        this.waypoints.forEach((waypoint, i) => {
            const lat = waypoint.getLatLng().lat
            const lon = waypoint.getLatLng().lng

            if (i) {
                routeLength += GeoUtils.calculateDistance(
                    this.waypoints[i - 1].getLatLng().lat,
                    this.waypoints[i - 1].getLatLng().lng,
                    lat,
                    lon
                );
            }

            let leg = {
                lat: lat,
                lon: lon,
                radius: 120,
                xteLeft: 120,
                xteRight: 120,
                info: {
                    course: 0,
                    fromPreviousMeters: 0,
                    fromStartMeters: 0,
                    toFinishMeters: 0,
                    fromPreviousSeconds: 0,
                    fromStartSeconds: 0,
                    toFinishSeconds: 0
                },
                isDepthSafe: true,
                overallSafety: "safe"
            }

            let legInfo = {
                course: 0,
                fromPreviousMeters: 0,
                fromStartMeters: 0,
                toFinishMeters: 0,
                fromPreviousSeconds: 0,
                fromStartSeconds: 0,
                toFinishSeconds: 0
            }

            let legSafety = {
                lat: lat,
                lon: lon,
                safety: 1
            }

            route.push(leg)
            info.push(legInfo)
            safety.push(legSafety)
        })

        this.routesStore.addShipRouteResponse({
            routePoints: route,
            routePointInfos: info,
            routeLegs: safety,
            calculatedRouteLegs: route,
            routeLength: routeLength
        }, true, updateRouteTable)


    }

    // Удаляем все точки маршрута
    cancelAutorouting() {
        this.clearRoute()
        this.isAutoroutingCancel = true
    }

    // Перерисовываем маршрут
    redrawRoute() {
        if (!this.map) {
            console.warn('redrawRoute: map is null, cannot redraw route');
            return;
        }
        
        this.clearRoute(false, false, false);

        const currentWaypoints = this.routesStore.getShipWaypoints;
        if (currentWaypoints) {
            currentWaypoints.forEach(waypoint => {
                waypoints.addWP.call(this, L.latLng(waypoint.lat, waypoint.lon))
            })
        }

        legs.drawLegs.call(this, {
            updateRouteTable: true, 
            makeRouteTable: false
        })
    }

    // Центрируем карту на выбранном корабле
    centerOnSelectedShip() {
        if (!this.map) {
            console.warn('centerOnSelectedShip: map not ready');
            return false;
        }

        // Если есть позиция корабля из fleet
        if (this.fleetStore.selectedShipPosition) {
            const { lat, lon, cog } = this.fleetStore.selectedShipPosition;

            // Центрируем карту на позиции корабля
            this.map.setView([lat, lon], 12);

            const shipIcon = createShipIcon({
                width: 20,
                height: 40,
                headingDeg: cog || 0,
                scale: 0.72,
                fillColor: '#1d4ed8',
                strokeColor: '#bfdbfe',
                strokeWidth: 1.4,
                className: 'ship-icon-container',
                alt: 'Ship',
            });

            L.marker([lat, lon], { icon: shipIcon }).addTo(this.map);

            return true;
        }

        return false; // Если нет позиции
    }    
    
    // Переключаем видимость AIO слоя
    toggleAIOLayer(isVisible) {
        this.isAIOVisible = isVisible;

        // Всегда удаляем предыдущий слой, чтобы избежать дублирования
        if (this.aioLayer && this.aioLayer.overlays.AIO && this.map.hasLayer(this.aioLayer.overlays.AIO)) {
            this.map.removeLayer(this.aioLayer.overlays.AIO);
        }

        // if (isVisible && this.aioLayer) {
        //     // Загружаем и отображаем AIO при включении слоя
        //     this.loadAndShowAIO();
        // }
    }

    // Загружаем AIO данные и отображаем их на карте
    async loadAndShowAIO() {
        if (!this.aioLayer) {
            console.error('AIO layer not initialized');
            return;
        }
        
        if (!this.fleetStore) {
            console.error('Fleet store not initialized');
            return;
        }
        
        try {
            // Получаем AIO данные из store
            const aioData = this.fleetStore.data.aio || {};
            console.log('AIO data from store:', aioData);
            
            // Проверяем, что данные не пустые
            if (!aioData.tempInfos && !aioData.permanentInfos) {
                console.warn('No AIO data found in store');
                // Скрываем индикатор загрузки если данных нет
                eventBus.$emit('aio-loading-end');
                return;
            }
            
            // Отображаем AIO объекты на карте
            const aioLayerGroup = await this.aioLayer.addAIOLayer();
            
            // Добавляем слой на карту
            if (aioLayerGroup && !this.map.hasLayer(aioLayerGroup)) {
                this.map.addLayer(aioLayerGroup);
            } else if (aioLayerGroup && this.map.hasLayer(aioLayerGroup)) {
                console.log('AIO layer already on map');
            } else {
                console.warn('AIO layer group is null or undefined');
            }
            console.log('AIO objects loaded on routes map');
            
            // Скрываем индикатор загрузки после того как отобразили объекты
            eventBus.$emit('aio-loading-end');
            
        } catch (error) {
            console.error('Error loading AIO on routes map:', error);
            // Скрываем индикатор загрузки если ошибка
            eventBus.$emit('aio-loading-end');
        }
    }

    // Показываем AIO объект на карте и центрируем карту на нем
    showAIOOnMap(aioItem) {
        if (!this.aioLayer || !this.isAIOVisible) {
            // Если AIO слой выключен, включаем его
            this.toggleAIOLayer(true);
            
            // Эмитим событие для обновления состояния в UI
            eventBus.$emit('aio-layer-enabled');
        }
        
        if (aioItem && aioItem.geometry) {
            try {
                // Парсим геометрию и центрируем карту
                const coordinates = this.parseAIOGeometry(aioItem.geometry);
                if (coordinates && coordinates.length > 0) {
                    const bounds = L.latLngBounds(coordinates);
                    this.map.fitBounds(bounds, { padding: [20, 20] });
                }
            } catch (error) {
                console.error('Error showing AIO item on map:', error);
            }
        }
    }

    // Парсим геометрию AIO объекта из строки в формате POLYGON
    parseAIOGeometry(geometryString) {
        if (!geometryString || typeof geometryString !== 'string') {
            return null;
        }

        try {
            // Ищем координаты в формате POLYGON((lon lat, lon lat, ...))
            const match = geometryString.match(/POLYGON\s*\(\s*\(([^)]+)\)\s*\)/i);
            if (!match) {
                console.warn('Invalid polygon geometry format:', geometryString);
                return null;
            }

            const coordsString = match[1];
            const coordinates = coordsString.split(',').map(pair => {
                const [lon, lat] = pair.trim().split(/\s+/).map(Number);
                return [lat, lon];
            }).filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));

            return coordinates;
        } catch (error) {
            console.error('Error parsing AIO geometry:', error);
            return null;
        }
    }

    // Создаем необходимые панели для AIO объектов
    createAIOPanes() {
        if (!this.map) {
            console.warn('createAIOPanes: map not ready');
            return;
        }
        
        // Создаем панель для AIO объектов если она не существует
        if (!this.map.getPane('aio')) {
            this.map.createPane('aio');
            this.map.getPane('aio').style.zIndex = 650; // Устанавливаем z-index выше базовых слоев
        }
    }

    // Создаем необходимые панели для Navarea объектов
    createNavareaPanes() {
        if (!this.map) {
            console.warn('createNavareaPanes: map not ready');
            return;
        }
        
        // Создаем панель для Navarea полигонов если она не существует
        if (!this.map.getPane('navarea')) {
            this.map.createPane('navarea');
            this.map.getPane('navarea').style.zIndex = 655; // Устанавливаем z-index немного выше AIO
        }
        
        // Создаем панель для маркеров Navarea если она не существует
        if (!this.map.getPane('navarea-markers')) {
            this.map.createPane('navarea-markers');
            this.map.getPane('navarea-markers').style.zIndex = 660; // Для маркеров еще выше
        }
        
        // Создаем панель для MarkerCluster если она не существует
        if (!this.map.getPane('markerPane')) {
            this.map.createPane('markerPane');
            this.map.getPane('markerPane').style.zIndex = 665; // Для кластеров маркеров
        }
        
        console.log('Navarea panes created:', {
            navarea: !!this.map.getPane('navarea'),
            'navarea-markers': !!this.map.getPane('navarea-markers'),
            markerPane: !!this.map.getPane('markerPane')
        });
    }    
    
    // Переключаем видимость Navarea слоя
    toggleNavareaLayer(isVisible) {
        this.isNavareaVisible = isVisible;

        // Всегда удаляем предыдущий слой, чтобы избежать дублирования
        if (this.navareaLayer && this.navareaLayer.overlays.navarea && this.map.hasLayer(this.navareaLayer.overlays.navarea)) {
            this.map.removeLayer(this.navareaLayer.overlays.navarea);
        }
        
        if (isVisible && this.navareaLayer) {
            // Загружаем и отображаем Navarea при включении слоя
            this.loadAndShowNavarea();
        }
    }

    // Загружаем Navarea данные и отображаем их на карте
    async loadAndShowNavarea() {
        if (!this.navareaLayer) {
            console.error('Navarea layer not initialized');
            return;
        }
        
        if (!this.fleetStore) {
            console.error('Fleet store not initialized');
            return;
        }
        
        try {
            // Получаем Navarea данные из store
            const navareaData = this.fleetStore.data.navarea || [];
            console.log('Navarea data from store:', navareaData);

            // Проверяем, что данные не пустые
            let hasValidData = false;
            if (Array.isArray(navareaData) && navareaData.length > 0) {
                hasValidData = true;
                console.log('✅ Route-leaf: Found array format data');
            }
            
            if (!hasValidData) {
                console.warn('❌ No Navarea data found in store');
                // Скрываем индикатор загрузки если данных нет
                eventBus.$emit('navarea-loading-end');
                return;
            }
            
            // Отображаем Navarea объекты на карте
            const navareaLayerGroup = await this.navareaLayer.addNavareaLayer();
            
            // Добавляем слой на карту
            if (navareaLayerGroup && !this.map.hasLayer(navareaLayerGroup)) {
                this.map.addLayer(navareaLayerGroup);
            } else if (navareaLayerGroup && this.map.hasLayer(navareaLayerGroup)) {
                console.log('Navarea layer already on map');
            } else {
                console.warn('Navarea layer group is null or undefined');
            }
            
            console.log('Navarea objects loaded on routes map');
            
            // Скрываем индикатор загрузки после того как отобразили объекты
            eventBus.$emit('navarea-loading-end');
            
        } catch (error) {
            console.error('Error loading Navarea on routes map:', error);
            // Скрываем индикатор загрузки если ошибка
            eventBus.$emit('navarea-loading-end');
        }    }

    // Показываем Navarea объект на карте и центрируем карту на нем
    showNavareaOnMap(navareaItem) {
        if (!this.navareaLayer || !this.isNavareaVisible) {
            // Если Navarea слой выключен, включаем его
            this.toggleNavareaLayer(true);
            
            // Эмитим событие для обновления состояния в UI
            eventBus.$emit('navarea-layer-enabled');
        }
        
        if (navareaItem) {
            try {
                console.log('🗺️ Route-leaf: Showing navarea item on map:', navareaItem);
                
                // Проверяем наличие координат в новом формате
                let hasCoordinates = false;
                
                if (navareaItem.coordinates && 
                    (navareaItem.coordinates.decimal_coordinates || 
                     navareaItem.coordinates.geojson_coordinates)) {
                    hasCoordinates = true;
                    console.log('✅ Route-leaf: Found coordinates in new format');
                }
                // Проверяем старый формат для совместимости
                else if (navareaItem.locationPoints || navareaItem.locationAreas) {
                    hasCoordinates = true;
                    console.log('✅ Route-leaf: Found coordinates in old format');
                }
                // Пробуем найти координаты в свойстве data (старый формат)
                else if (navareaItem.data) {
                    if (navareaItem.data.locationPoints || navareaItem.data.locationAreas) {
                        navareaItem = {
                            ...navareaItem,
                            locationPoints: navareaItem.data.locationPoints || [],
                            locationAreas: navareaItem.data.locationAreas || []
                        };
                        hasCoordinates = true;
                        console.log('✅ Route-leaf: Found coordinates in data property');
                    }
                }
                
                if (hasCoordinates) {
                    // Центрируем карту на Navarea объекте
                    const bounds = this.getNavareaItemBounds(navareaItem);
                    if (bounds) {
                        this.map.fitBounds(bounds, { padding: [20, 20] });
                        console.log('✅ Route-leaf: Map centered on navarea item');
                    } else {
                        // Если не удалось получить границы, выводим ошибку
                        console.error('❌ Failed to get bounds for Navarea item:', navareaItem);
                    }
                } else {
                    console.error('❌ Navarea item has no location data:', navareaItem);
                }
            } catch (error) {
                console.error('❌ Error showing Navarea item on map:', error);
            }
        }
    }

    // Получаем границы Navarea объекта для центрирования карты
    getNavareaItemBounds(navareaItem) {
        const coordinates = [];
        
        // Обрабатываем новый формат координат
        if (navareaItem.coordinates) {
            let coords = null;
            if (navareaItem.coordinates.decimal_coordinates) {
                coords = navareaItem.coordinates.decimal_coordinates;
            } else if (navareaItem.coordinates.geojson_coordinates) {
                coords = navareaItem.coordinates.geojson_coordinates;
            }
            
            if (coords && Array.isArray(coords)) {
                coords.forEach(coord => {
                    if (Array.isArray(coord) && coord.length >= 2) {
                        console.log('🗺️ Route-leaf processing coord:', coord);
                        
                        const first = parseFloat(coord[0]);
                        const second = parseFloat(coord[1]);
                        
                        let lat, lon;
                        // Определяем формат координат
                        if (Math.abs(first) <= 90 && Math.abs(second) <= 180) {
                            // Вероятно формат [lat, lon]
                            lat = first;
                            lon = second;
                            console.log('🔄 Route-leaf: Assuming [lat, lon] format');
                        } else {
                            // Стандартный GeoJSON формат [lon, lat]
                            lat = second;
                            lon = first;
                            console.log('📋 Route-leaf: Using GeoJSON [lon, lat] format');
                        }
                        
                        if (!isNaN(lat) && !isNaN(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) {
                            coordinates.push([lat, lon]);
                        }
                    } else if (coord.lat !== undefined && coord.lon !== undefined) {
                        // Координаты в формате объекта {lat, lon}
                        const lat = parseFloat(coord.lat);
                        const lon = parseFloat(coord.lon);
                        if (!isNaN(lat) && !isNaN(lon)) {
                            coordinates.push([lat, lon]);
                        }
                    }
                });
            }
        }
        
        // Добавляем точки из locationPoints (старый формат)
        if (navareaItem.locationPoints && navareaItem.locationPoints.length > 0) {
            navareaItem.locationPoints.forEach(point => {
                const lat = parseFloat(point.lat);
                const lon = parseFloat(point.lon);
                if (!isNaN(lat) && !isNaN(lon)) {
                    coordinates.push([lat, lon]);
                }
            });
        }
          // Добавляем точки из locationAreas
        if (navareaItem.locationAreas && navareaItem.locationAreas.length > 0) {
            navareaItem.locationAreas.forEach(area => {
                if (area.points) {
                    // Если points это массив, обрабатываем каждую точку
                    if (Array.isArray(area.points)) {
                        area.points.forEach(point => {
                            const lat = parseFloat(point.lat);
                            const lon = parseFloat(point.lon);
                            if (!isNaN(lat) && !isNaN(lon)) {
                                coordinates.push([lat, lon]);
                            }
                        });
                    } 
                    // Если points это объект с одной точкой
                    else if (area.points.lat && area.points.lon) {
                        const lat = parseFloat(area.points.lat);
                        const lon = parseFloat(area.points.lon);
                        if (!isNaN(lat) && !isNaN(lon)) {
                            coordinates.push([lat, lon]);
                        }
                    }
                }
            });
        }
        
        if (coordinates.length > 0) {
            return L.latLngBounds(coordinates);
        }
        return null;
    }

    // Принудительная очистка всех overlay-ев
    clearAllOverlays() {
        if (!this.map) return;
        
        // Очищаем AIO слой
        if (this.aioLayer && this.aioLayer.overlays && this.aioLayer.overlays.AIO) {
            if (this.map.hasLayer(this.aioLayer.overlays.AIO)) {
                this.map.removeLayer(this.aioLayer.overlays.AIO);
            }
        }
        
        // Очищаем Navarea слой
        if (this.navareaLayer && this.navareaLayer.overlays && this.navareaLayer.overlays.navarea) {
            if (this.map.hasLayer(this.navareaLayer.overlays.navarea)) {
                this.map.removeLayer(this.navareaLayer.overlays.navarea);
            }
        }
        
        // Сбрасываем флаги видимости
        this.isAIOVisible = false;
        this.isNavareaVisible = false;
    }

    // Метод для полной очистки карты и освобождения ресурсов
    destroy() {
        try {
            // Принудительно очищаем все оверлеи
            this.clearAllOverlays();
            
            // Очищаем маршрут БЕЗ очистки store при уничтожении компонента
            // Сохраняем данные маршрута для других вкладок
            this.clearRoute(false, false, false);
            
            // Очищаем AIO слой
            if (this.aioLayer) {
                this.aioLayer.destroy && this.aioLayer.destroy();
                this.aioLayer = null;
            }
            
            // Очищаем Navarea слой
            if (this.navareaLayer) {
                this.navareaLayer.destroy && this.navareaLayer.destroy();
                this.navareaLayer = null;
            }
            
            // Очищаем depthsList
            if (this.depthsList) {
                this.depthsList.clearLayers();
                if (this.map && this.map.hasLayer(this.depthsList)) {
                    this.map.removeLayer(this.depthsList);
                }
                this.depthsList = null;
            }
            
            // Сбрасываем флаги
            this.isAIOVisible = false;
            this.isNavareaVisible = false;
            
            // Удаляем карту (вызываем родительский метод, если есть)
            if (this.map) {
                this.map.remove();
                this.map = null;
            }
            
            console.log('RouteLeaf: все ресурсы очищены');
        } catch (error) {
            console.warn('RouteLeaf: ошибка при очистке ресурсов', error);
        }
    }
}
